import { Star, Camera } from "lucide-react";
import { AnalyticsInputIndicator } from "../../../../../components/analytics/AnalyticsInputIndicator";
import { Button } from "../../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { type CitationDocument } from "./types";

interface EvidenceUsedSectionProps {
  citationDocuments: CitationDocument[];
  onDocumentClick: (documentEntry: CitationDocument) => void;
}

export function EvidenceUsedSection({
  citationDocuments,
  onDocumentClick,
}: EvidenceUsedSectionProps) {
  if (citationDocuments.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="text-md font-semibold mb-2">Evidence Used</div>
      <div className="flex flex-wrap gap-2 relative">
        <TooltipProvider>
          {citationDocuments.map((documentEntry, index) => {
            const name = documentEntry.document.name || "";
            const hasXlsx = /xlsx/i.test(name);
            const hasScreenshot =
              /screenshot/i.test(name) || /screen[\s_-]?shot/i.test(name);
            const isExcelScreenshot = hasXlsx && hasScreenshot;
            const isKeyCitation = documentEntry.citations.some(
              (c) => c.isKey === true,
            );
            const summary =
              documentEntry.citations[0]?.shortSummary ||
              "No summary available";

            return (
              <Tooltip
                key={`${documentEntry.document.id}-${documentEntry.citations[0]?.id || index}`}
              >
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="max-w-xs overflow-hidden border bg-stone-100 hover:bg-stone-200 text-stone-800 border-stone-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDocumentClick(documentEntry);
                    }}
                  >
                    <span className="flex items-center gap-2 truncate w-full text-left">
                      {isKeyCitation && (
                        <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />
                      )}
                      {isExcelScreenshot && (
                        <Camera className="h-4 w-4 shrink-0" />
                      )}
                      <span className="truncate">
                        {documentEntry.document.name}
                      </span>
                      <AnalyticsInputIndicator
                        flagged={documentEntry.document.isAnalyticsInput}
                      />
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    {isKeyCitation && (
                      <div className="text-amber-600 text-xs mb-1 flex items-center gap-1">
                        <Star className="h-3 w-3 fill-amber-400" />
                        Include in WP
                      </div>
                    )}
                    {isExcelScreenshot && (
                      <div className="text-amber-700 text-xs mb-1 flex items-center gap-1">
                        <Camera className="h-3.5 w-3.5" />
                        Detected as screenshot
                      </div>
                    )}
                    <div>{summary}</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>
    </div>
  );
}
